/**
 * The morph shapes shared by /morph-catalog and /morph-particle: every primitive the MorphParticle can take
 * with its parameters — size, depth, trapezoid X/Y/Z and the particle shape of wave-engine (roundness, hemi,
 * platonic solids; #5565).
 */

import * as THREE from 'three';
import type { ParticleShape } from '@arkturian/wave-engine/core/LayoutWriter';
import type { MorphBase } from '@arkturian/wave-engine/core/MorphBase';
import type { LayoutResult } from '@arkturian/wave-engine/core/types';

export interface ShapePreset {
    name: string;
    sizeX: number; sizeY: number; depth: number;
    trapX: number; trapY: number; trapZ: number;
    /** Base form of the geometry; default cube. */
    base?: MorphBase;
    /** roundness (0 = sharp base form … 1 = sphere) and hemi (morphTo `shape`); none = sharp. */
    shape?: ParticleShape;
}

/** Subdivisions for a showcase particle: per cube side, per solid triangle — enough for round edges and the sphere. */
export function showcaseSegments(base: MorphBase = 'cube', closeUp = false): number {
    return (base === 'cube' ? 12 : 8) * (closeUp ? 2 : 1);
}

export const SHAPE_PRESETS: ShapePreset[] = [
    // Row 1: Basic forms
    { name: 'Flat Card', sizeX: 1, sizeY: 1, depth: 0.02, trapX: 0, trapY: 0, trapZ: 0 },
    { name: 'Cube', sizeX: 0.8, sizeY: 0.8, depth: 0.8, trapX: 0, trapY: 0, trapZ: 0 },
    { name: 'Cuboid', sizeX: 1.2, sizeY: 0.6, depth: 0.4, trapX: 0, trapY: 0, trapZ: 0 },
    { name: 'Pillar', sizeX: 0.3, sizeY: 1.2, depth: 0.3, trapX: 0, trapY: 0, trapZ: 0 },
    { name: 'Slab', sizeX: 1.2, sizeY: 0.3, depth: 0.8, trapX: 0, trapY: 0, trapZ: 0 },

    // Row 2: Trapezoid X (narrows top/bottom in X)
    { name: 'Trapezoid', sizeX: 1, sizeY: 0.8, depth: 0.3, trapX: 0.5, trapY: 0, trapZ: 0 },
    { name: 'Triangle', sizeX: 1, sizeY: 1, depth: 0.3, trapX: 1.0, trapY: 0, trapZ: 0 },
    { name: 'Inv Triangle', sizeX: 1, sizeY: 1, depth: 0.3, trapX: -1.0, trapY: 0, trapZ: 0 },
    { name: 'Wedge Thin', sizeX: 1, sizeY: 0.8, depth: 0.05, trapX: 0.7, trapY: 0, trapZ: 0 },
    { name: 'Arrow ▲', sizeX: 0.6, sizeY: 1.2, depth: 0.2, trapX: 1.0, trapY: 0, trapZ: 0 },

    // Row 3: Trapezoid Z (narrows in depth direction)
    { name: 'Wedge Z', sizeX: 1, sizeY: 0.8, depth: 0.6, trapX: 0, trapY: 0, trapZ: 0.8 },
    { name: 'Inv Wedge Z', sizeX: 1, sizeY: 0.8, depth: 0.6, trapX: 0, trapY: 0, trapZ: -0.8 },
    { name: 'Blade', sizeX: 1.2, sizeY: 0.2, depth: 0.5, trapX: 0, trapY: 0, trapZ: 1.0 },
    { name: 'Chisel', sizeX: 0.4, sizeY: 1, depth: 0.5, trapX: 0, trapY: 0, trapZ: 0.9 },
    { name: 'Prism Z', sizeX: 0.8, sizeY: 0.8, depth: 0.8, trapX: 0, trapY: 0, trapZ: 1.0 },

    // Row 4: Combined trapezoids → Pyramids, Obelisks
    { name: 'Pyramid', sizeX: 1, sizeY: 1, depth: 1, trapX: 1.0, trapY: 0, trapZ: 1.0 },
    { name: 'Obelisk', sizeX: 0.5, sizeY: 1.2, depth: 0.5, trapX: 0.6, trapY: 0, trapZ: 0.6 },
    { name: 'Frustum', sizeX: 1, sizeY: 0.8, depth: 0.8, trapX: 0.4, trapY: 0, trapZ: 0.4 },
    { name: 'Diamond', sizeX: 0.8, sizeY: 1, depth: 0.3, trapX: 0.8, trapY: 0, trapZ: 0 },
    { name: 'Crystal', sizeX: 0.5, sizeY: 1.4, depth: 0.5, trapX: 0.9, trapY: 0, trapZ: 0.9 },

    // Row 5: Trapezoid Y + mixed
    { name: 'Trap Y', sizeX: 1, sizeY: 0.8, depth: 0.5, trapX: 0, trapY: 0.6, trapZ: 0 },
    { name: 'Inv Trap Y', sizeX: 1, sizeY: 0.8, depth: 0.5, trapX: 0, trapY: -0.6, trapZ: 0 },
    { name: 'Roof', sizeX: 1.2, sizeY: 0.5, depth: 0.6, trapX: 0.8, trapY: 0, trapZ: 0 },
    { name: 'Gem', sizeX: 0.6, sizeY: 0.6, depth: 0.6, trapX: 0.7, trapY: 0.5, trapZ: 0.7 },
    { name: 'Spike', sizeX: 0.3, sizeY: 1.5, depth: 0.3, trapX: 1.0, trapY: 0, trapZ: 1.0 },

    // Row 6: particle shape (wave-engine 0.7): roundness + hemi
    { name: 'Sphere', sizeX: 1, sizeY: 1, depth: 1, trapX: 0, trapY: 0, trapZ: 0, shape: { roundness: 1 } },
    { name: 'Dome', sizeX: 1, sizeY: 1, depth: 1, trapX: 0, trapY: 0, trapZ: 0, shape: { roundness: 1, hemi: 1 } },
    { name: 'Ellipsoid', sizeX: 1.2, sizeY: 0.6, depth: 0.6, trapX: 0, trapY: 0, trapZ: 0, shape: { roundness: 1 } },
    { name: 'Disc', sizeX: 1, sizeY: 1, depth: 0.12, trapX: 0, trapY: 0, trapZ: 0, shape: { roundness: 1 } },
    { name: 'Soft Cube', sizeX: 0.8, sizeY: 0.8, depth: 0.8, trapX: 0, trapY: 0, trapZ: 0, shape: { roundness: 0.35 } },

    // Row 7: shape × trapezoid
    { name: 'Egg', sizeX: 0.8, sizeY: 1.1, depth: 0.8, trapX: 0.35, trapY: 0, trapZ: 0, shape: { roundness: 1 } },
    { name: 'Cone', sizeX: 1, sizeY: 1.2, depth: 1, trapX: 1.0, trapY: 0, trapZ: 0, shape: { roundness: 1 } },
    { name: 'Lens', sizeX: 1, sizeY: 1, depth: 0.35, trapX: 0, trapY: 0, trapZ: 0, shape: { roundness: 1, hemi: 1 } },
    { name: 'Pillow', sizeX: 1.2, sizeY: 0.8, depth: 0.35, trapX: 0, trapY: 0, trapZ: 0, shape: { roundness: 0.6 } },
    { name: 'Drop', sizeX: 0.8, sizeY: 1.2, depth: 0.8, trapX: 1.0, trapY: 0, trapZ: 1.0, shape: { roundness: 0.7 } },

    // Row 8: platonic solids as base form (wave-engine 0.9): sharp at roundness 0
    { name: 'Tetrahedron', sizeX: 1, sizeY: 1, depth: 1, trapX: 0, trapY: 0, trapZ: 0, base: 'tetrahedron' },
    { name: 'Octahedron', sizeX: 1, sizeY: 1, depth: 1, trapX: 0, trapY: 0, trapZ: 0, base: 'octahedron' },
    { name: 'Dodecahedron', sizeX: 1, sizeY: 1, depth: 1, trapX: 0, trapY: 0, trapZ: 0, base: 'dodecahedron' },
    { name: 'Icosahedron', sizeX: 1, sizeY: 1, depth: 1, trapX: 0, trapY: 0, trapZ: 0, base: 'icosahedron' },
    { name: 'Bipyramid', sizeX: 0.7, sizeY: 1.4, depth: 0.7, trapX: 0, trapY: 0, trapZ: 0, base: 'octahedron' },

    // Row 9: solids × size, trapezoid, dome, roundness
    { name: 'Dodeca Coin', sizeX: 1, sizeY: 1, depth: 0.2, trapX: 0, trapY: 0, trapZ: 0, base: 'dodecahedron' },
    { name: 'Gem Cut', sizeX: 1, sizeY: 1, depth: 0.8, trapX: 0.45, trapY: 0, trapZ: 0, base: 'icosahedron' },
    { name: 'Geo Dome', sizeX: 1, sizeY: 1, depth: 1, trapX: 0, trapY: 0, trapZ: 0, base: 'icosahedron', shape: { hemi: 1 } },
    { name: 'Soft Dodeca', sizeX: 1, sizeY: 1, depth: 1, trapX: 0, trapY: 0, trapZ: 0, base: 'dodecahedron', shape: { roundness: 0.35 } },
    { name: 'Spindle', sizeX: 0.45, sizeY: 1.5, depth: 0.45, trapX: 0, trapY: 0, trapZ: 0, base: 'octahedron' },

    // Row 10: rounded solids (roundness = edges round off, faces stay flat)
    { name: 'Soft Tetra', sizeX: 1, sizeY: 1, depth: 1, trapX: 0, trapY: 0, trapZ: 0, base: 'tetrahedron', shape: { roundness: 0.3 } },
    { name: 'Soft Octa', sizeX: 1, sizeY: 1, depth: 1, trapX: 0, trapY: 0, trapZ: 0, base: 'octahedron', shape: { roundness: 0.3 } },
    { name: 'Pebble', sizeX: 1.1, sizeY: 0.8, depth: 0.6, trapX: 0, trapY: 0, trapZ: 0, base: 'icosahedron', shape: { roundness: 0.5 } },
    { name: 'Rounded Box', sizeX: 1.1, sizeY: 0.8, depth: 0.6, trapX: 0, trapY: 0, trapZ: 0, shape: { roundness: 0.25 } },
    { name: 'Bead', sizeX: 0.8, sizeY: 0.8, depth: 0.8, trapX: 0, trapY: 0, trapZ: 0, base: 'dodecahedron', shape: { roundness: 0.6 } },

    // Row 11: cube rounded per edge group (wave-engine 0.10): outline (XY) and caps (Z) separately
    { name: 'Coin', sizeX: 1, sizeY: 1, depth: 0.15, trapX: 0, trapY: 0, trapZ: 0, shape: { roundnessXY: 1, roundnessZ: 0 } },
    { name: 'Cylinder', sizeX: 0.8, sizeY: 0.8, depth: 1.1, trapX: 0, trapY: 0, trapZ: 0, shape: { roundnessXY: 1, roundnessZ: 0 } },
    { name: 'Puck', sizeX: 1, sizeY: 1, depth: 0.35, trapX: 0, trapY: 0, trapZ: 0, shape: { roundnessXY: 1, roundnessZ: 0.3 } },
    { name: 'Capsule', sizeX: 0.6, sizeY: 0.6, depth: 1.3, trapX: 0, trapY: 0, trapZ: 0, shape: { roundnessXY: 1, roundnessZ: 0.7 } },
    { name: 'Tile', sizeX: 1, sizeY: 1, depth: 0.2, trapX: 0, trapY: 0, trapZ: 0, shape: { roundnessXY: 0.3, roundnessZ: 0 } },

    // Row 12: one edge group soft, the other hard
    { name: 'Soft Sides', sizeX: 0.9, sizeY: 0.9, depth: 0.9, trapX: 0, trapY: 0, trapZ: 0, shape: { roundnessXY: 0.35, roundnessZ: 0 } },
    { name: 'Soft Rims', sizeX: 0.9, sizeY: 0.9, depth: 0.9, trapX: 0, trapY: 0, trapZ: 0, shape: { roundnessXY: 0, roundnessZ: 0.35 } },
    { name: 'Round Slab', sizeX: 1.1, sizeY: 1.1, depth: 0.25, trapX: 0, trapY: 0, trapZ: 0, shape: { roundnessXY: 1, roundnessZ: 0.15 } },
    { name: 'Barrel', sizeX: 0.8, sizeY: 0.8, depth: 1.2, trapX: 0, trapY: 0, trapZ: 0, shape: { roundnessXY: 1, roundnessZ: 0.5 } },
    { name: 'Wedge Coin', sizeX: 1, sizeY: 1, depth: 0.3, trapX: 0.5, trapY: 0, trapZ: 0, shape: { roundnessXY: 1, roundnessZ: 0 } },
];

/** Label suffix for the shape part: the base form, R(oundness), H(emi). */
export function shapeLabel(p: ShapePreset): string {
    const parts = [p.base && p.base !== 'cube' ? p.base : '', p.shape?.roundness ? `R${p.shape.roundness}` : '',
        p.shape?.roundnessXY !== undefined ? `Rxy${p.shape.roundnessXY}` : '', p.shape?.roundnessZ !== undefined ? `Rz${p.shape.roundnessZ}` : '',
        p.shape?.hemi ? `H${p.shape.hemi}` : ''].filter(Boolean);
    return parts.length ? ` ${parts.join(' ')}` : '';
}

export function presetParams(p: ShapePreset): string {
    const trap = (p.trapX || p.trapY || p.trapZ) ? ` T(${p.trapX},${p.trapY},${p.trapZ})` : '';
    return `${p.sizeX}×${p.sizeY}×${p.depth}${trap}${shapeLabel(p)}`;
}

/** One particle at the origin in the preset's form, as a layout result for morphTo. */
export function presetResult(p: ShapePreset): LayoutResult {
    return {
        target: {
            position: new THREE.Vector3(), quaternion: new THREE.Quaternion(), scale: new THREE.Vector3(1, 1, 1),
            size: new THREE.Vector2(p.sizeX, p.sizeY), trapezoid: new THREE.Vector2(p.trapX, p.trapY),
            morph: { sizeX: p.sizeX, sizeY: p.sizeY, depth: p.depth, trapezoidX: p.trapX, trapezoidY: p.trapY, trapezoidZ: p.trapZ },
        },
        current: null as unknown as LayoutResult['current'],
    } as LayoutResult;
}

// ─── Guide: how each setting is made, for people and for agents ─────────────────────────────────────────────────

export type ParamLevel = 'Geometrie (pro Mesh)' | 'Form (pro Layout, morphTo shape)' | 'Partikel (pro Instanz, LayoutResult)';

export interface ParamDoc { name: string; level: ParamLevel; range: string; what: string }

/** Every setting of a morph particle: where it is set, its range, what it does. */
export const PARAM_DOCS: ParamDoc[] = [
    { name: 'base', level: 'Geometrie (pro Mesh)', range: "'cube' | 'tetrahedron' | 'octahedron' | 'dodecahedron' | 'icosahedron'",
        what: 'Grundform. Ihre Kanten liegen exakt im Netz, bei Rundung 0 ist sie scharf. Ein anderer base braucht eine andere Geometrie; ein Partikel wechselt sie unsichtbar bei roundness 1 (dort sind alle die gleiche Kugel).' },
    { name: 'segments', level: 'Geometrie (pro Mesh)', range: '1 … 32',
        what: 'Unterteilung je Kante (Würfel: je Seite, Körper: je Dreieck). Nötig für Zwischenstufen der Rundung und für runde Umrisse von Scheiben/Zylindern. Die volle Kugel (roundness 1 auf dem Würfel, ohne hemi und Trapez) ist eine Ausnahme: ihr Umriss wird pro Pixel exakt berechnet und ist bei jeder Unterteilung rund, auch bei 1. Kosten = Instanzen × Dreiecke: Würfel 1 = 12, 4 = 192, 16 = 3 072 Dreiecke. Quest ≈ 1–2 Mio Dreiecke pro Bild.' },
    { name: 'roundness', level: 'Form (pro Layout, morphTo shape)', range: '0 … 1',
        what: '0 = Grundform mit scharfen Kanten, dann runden sich die Kanten (Flächen bleiben flach), 1 = Kugel. Die Rundungsbreite wächst mit roundness², 1 % ist kaum sichtbar. Setzt beim Würfel roundnessXY und roundnessZ zugleich.' },
    { name: 'roundnessXY', level: 'Form (pro Layout, morphTo shape)', range: '0 … 1 (nur Würfel)',
        what: 'Die 4 Kanten, die von vorne nach hinten laufen (parallel zur Tiefe z) — also der Umriss in der Ansicht von vorne. 1 = Kreis.' },
    { name: 'roundnessZ', level: 'Form (pro Layout, morphTo shape)', range: '0 … 1 (nur Würfel)',
        what: 'Die 8 Kanten um Vorder- und Rückseite. 0 = harte Kante. Scheibe/Zylinder = { roundnessXY: 1, roundnessZ: 0 }, Tiefe bestimmt flach oder lang.' },
    { name: 'hemi', level: 'Form (pro Layout, morphTo shape)', range: '0 … 1',
        what: 'Halbform: die hintere Hälfte wird auf z = 0 geklappt — Kuppel nach vorne mit flacher Rückseite (Halbkugel bei roundness 1).' },
    { name: 'sizeX / sizeY', level: 'Partikel (pro Instanz, LayoutResult)', range: '> 0 (Welteinheiten)',
        what: 'Breite und Höhe des Partikels. Für Kugeln/Bubbles sizeX = sizeY = depth = Durchmesser, sonst Ellipsoid.' },
    { name: 'depth', level: 'Partikel (pro Instanz, LayoutResult)', range: '> 0',
        what: 'Tiefe (lokale z-Achse, Vorderseite +z). uDepthDirection: 0 = symmetrisch, 1 = nur nach hinten (Vorderseite bleibt an der Position), 2 = nur nach vorne.' },
    { name: 'trapezoidX', level: 'Partikel (pro Instanz, LayoutResult)', range: '−1 … 1',
        what: 'Verengt die Breite entlang der Höhe: > 0 oben schmal, < 0 unten schmal, ±1 = Spitze (Dreieck). Keil im Ring: sizeX = Bogen am Außenradius, trapezoidX = −(1 − rInnen/rAußen), lokale +y nach außen.' },
    { name: 'trapezoidY', level: 'Partikel (pro Instanz, LayoutResult)', range: '−1 … 1',
        what: 'Verengt die Höhe entlang der Tiefe: > 0 zur Vorderseite hin schmal, < 0 zur Rückseite. (Im Datenmodus trägt dieser Platz den Datenwert.)' },
    { name: 'trapezoidZ', level: 'Partikel (pro Instanz, LayoutResult)', range: '−1 … 1',
        what: 'Verengt die Breite entlang der Tiefe: > 0 zur Vorderseite hin schmal, < 0 zur Rückseite. Mit trapezoidX zusammen: Pyramide, Obelisk.' },
    { name: 'position / quaternion / scale', level: 'Partikel (pro Instanz, LayoutResult)', range: '—',
        what: 'Ort, Drehung, gleichmäßige Skalierung. Die Drehung legt fest, welche Kanten „vorne“ sind — so wird z. B. die weiche Kantengruppe zur senkrechten.' },
];

export const GUIDE_RULES: string[] = [
    'Drei Ebenen: Geometrie einmal pro Mesh (base, segments) · Form pro Layout für alle Partikel eines Meshes (morphTo shape) · Maße pro Partikel im Layout (LayoutResult.target.morph).',
    'morphTo mischt alles zugleich: Position, Drehung, Maße, Trapez und Form. Ein abgebrochener Morph läuft vom sichtbaren Zwischenstand weiter.',
    'Ein übergebenes shape ist vollständig (fehlende Felder = 0); ohne shape bleibt die aktuelle Form.',
    'Verschiedene Formen gleichzeitig (z. B. Kugeln und Scheiben) = je Form ein eigenes Mesh mit eigenen Uniforms.',
    'Der Normalenverlauf wird bei 0 < Rundung < 1 pro Pixel berechnet; scharfe Formen und die Kugel kosten nichts extra.',
];

const fmt = (n: number) => String(Number(n.toFixed(3)));

/** The preset as code: geometry, morphTo shape, per-particle morph — copy-ready for people and agents. */
export function presetCode(p: ShapePreset): string {
    const base = p.base ?? 'cube';
    const segs = p.shape && (p.shape.roundness || p.shape.roundnessXY || p.shape.roundnessZ || p.shape.hemi)
        ? (base === 'cube' ? 4 : 8) : (base === 'cube' ? 1 : 4);
    const shape = p.shape ? Object.entries(p.shape).filter(([, v]) => v !== undefined).map(([k, v]) => `${k}: ${v}`).join(', ') : '';
    return [
        `// ${p.name}`,
        `const geometry = createMorphGeometry({ maxInstances: N, base: '${base}', segments: ${segs} });`,
        `morphTo(mesh, uniforms, results, { duration: 1.5, shape: { ${shape || 'roundness: 0'} } });`,
        `// je Partikel im Layout (LayoutResult.target.morph):`,
        `morph: { sizeX: ${fmt(p.sizeX)}, sizeY: ${fmt(p.sizeY)}, depth: ${fmt(p.depth)}, trapezoidX: ${fmt(p.trapX)}, trapezoidY: ${fmt(p.trapY)}, trapezoidZ: ${fmt(p.trapZ)} }`,
    ].join('\n');
}

/** The whole catalog as plain data (what an agent copies to pick a shape). */
export function catalogJson(): string {
    return JSON.stringify(SHAPE_PRESETS.map(p => ({
        name: p.name, base: p.base ?? 'cube', shape: p.shape ?? {},
        morph: { sizeX: p.sizeX, sizeY: p.sizeY, depth: p.depth, trapezoidX: p.trapX, trapezoidY: p.trapY, trapezoidZ: p.trapZ },
    })), null, 1);
}

